/**
 * @module Config
 * @description This file manages configuration settings for the application.
 */
import { getApp, getEnvironment } from 'src/config/environment';
import { App } from 'src/types/app';
import { Environment } from 'src/types/env';

type Config = {
  apiBaseUrl: string;
  auth0Domain: string;
  auth0ClientId: string;
  auth0Audience: string;
  auth0Namespace: string;
  auth0Connection: string;
  sentryTracesSampleRate: number;
  sentryEnvironment: string;
};

type ConfigMap = {
  [env in Environment]?: {
    [app in App]?: Config;
  };
};

const configMap: ConfigMap = {
  [Environment.Local]: {
    [App.Ldt]: {
      apiBaseUrl: 'http://localhost:3011',
      auth0Domain: 'test-auth.artera.ai',
      auth0ClientId: 'mmdaeBHS58zBRVdAyhOwWrCVai0gCIBH',
      auth0Audience: 'https://api.dev.arteraai.co',
      auth0Namespace: 'https://app.artera.ai',
      auth0Connection: 'artera-user-password-auth',
      sentryTracesSampleRate: 1.0,
      sentryEnvironment: 'react-ldt-local',
    },
    [App.SamdUs]: {
      apiBaseUrl: 'http://localhost:3011',
      auth0Domain: 'test-auth.artera.ai',
      auth0ClientId: '38VAbgQkIJYeKUJ9jxLxvXO7EJc6jeln',
      auth0Audience: 'https://api-us.dev.arteraai.co',
      auth0Namespace: 'https://app.artera.ai',
      auth0Connection: 'samd-artera-user-password-auth',
      sentryTracesSampleRate: 1.0,
      sentryEnvironment: 'react-samd-us-local',
    },
    [App.SamdEu]: {
      apiBaseUrl: 'http://localhost:3011',
      auth0Domain: 'staging-auth-eu.artera.ai',
      auth0ClientId: 'mb0MiiZYAs4f1Cv8bD9UWar0GEOjKIK0',
      auth0Audience: 'https://api-staging-eu.artera.ai',
      auth0Namespace: 'https://app-eu.artera.ai',
      auth0Connection: 'Username-Password-Authentication',
      sentryTracesSampleRate: 1.0,
      sentryEnvironment: 'react-samd-eu-local',
    },
    [App.SamdDemo]: {
      apiBaseUrl: 'http://localhost:3011',
      auth0Domain: 'test-auth.artera.ai',
      auth0ClientId: 'gVBo2zLMnFjmxr1hFo23vGoAkGBZ8P1b',
      auth0Audience: 'https://api-demo-us.dev.arteraai.co',
      auth0Namespace: 'https://app.artera.ai',
      auth0Connection: 'demo-artera-user-password-auth',
      sentryTracesSampleRate: 1.0,
      sentryEnvironment: 'react-samd-us-local',
    },
    [App.SamdUk]: {
      apiBaseUrl: 'http://localhost:3011',
      auth0Domain: 'staging-auth-uk.artera.ai',
      auth0ClientId: '2Ce3AUMnJTZx7jUc67w64ZzV8Ah23LHg',
      auth0Audience: 'https://api-staging-uk.artera.ai',
      auth0Namespace: 'https://app-uk.artera.ai',
      auth0Connection: 'Username-Password-Authentication',
      sentryTracesSampleRate: 1.0,
      sentryEnvironment: 'react-samd-uk-local',
    },
  },
  [Environment.Development]: {
    [App.Ldt]: {
      apiBaseUrl: 'https://api.dev.arteraai.co',
      auth0Domain: 'test-auth.artera.ai',
      auth0ClientId: 'mmdaeBHS58zBRVdAyhOwWrCVai0gCIBH',
      auth0Audience: 'https://api.dev.arteraai.co',
      auth0Namespace: 'https://app.artera.ai',
      auth0Connection: 'artera-user-password-auth',
      sentryTracesSampleRate: 1.0,
      sentryEnvironment: 'react-ldt-development',
    },
    [App.SamdUs]: {
      apiBaseUrl: 'https://api-us.dev.arteraai.co',
      auth0Domain: 'test-auth.artera.ai',
      auth0ClientId: '38VAbgQkIJYeKUJ9jxLxvXO7EJc6jeln',
      auth0Audience: 'https://api-us.dev.arteraai.co',
      auth0Namespace: 'https://app.artera.ai',
      auth0Connection: 'samd-artera-user-password-auth',
      sentryTracesSampleRate: 1.0,
      sentryEnvironment: 'react-samd-us-development',
    },
    [App.SamdDemo]: {
      apiBaseUrl: 'https://api-demo-us.dev.arteraai.co',
      auth0Domain: 'test-auth.artera.ai',
      auth0ClientId: 'gVBo2zLMnFjmxr1hFo23vGoAkGBZ8P1b',
      auth0Audience: 'https://api-demo-us.dev.arteraai.co',
      auth0Namespace: 'https://app.artera.ai',
      auth0Connection: 'demo-artera-user-password-auth',
      sentryTracesSampleRate: 1.0,
      // TODO(Troy): confirm we want to set up sentry for samd demo or reuse samd us configs
      sentryEnvironment: 'react-samd-us-development',
    },
  },
  [Environment.Staging]: {
    [App.Ldt]: {
      apiBaseUrl: 'https://api-staging.artera.ai',
      auth0Domain: 'staging-auth.artera.ai',
      auth0ClientId: '5tBCz440Z0yqrUiZOdXrXR8sM1n8h6QV',
      auth0Audience: 'https://api-staging.artera.ai',
      auth0Namespace: 'https://app.artera.ai',
      auth0Connection: 'artera-user-password-auth',
      sentryTracesSampleRate: 1.0,
      sentryEnvironment: 'react-ldt-staging',
    },
    [App.SamdUs]: {
      apiBaseUrl: 'https://api-staging-us.artera.ai',
      auth0Domain: 'staging-auth.artera.ai',
      auth0ClientId: 'Ykc9MbEw0ECRkNNQkoXYiEaTIG0sutX5',
      auth0Audience: 'https://api-staging-us.artera.ai',
      auth0Namespace: 'https://app.artera.ai',
      auth0Connection: 'samd-artera-user-password-auth',
      sentryTracesSampleRate: 1.0,
      sentryEnvironment: 'react-samd-us-staging',
    },
    [App.SamdEu]: {
      apiBaseUrl: 'https://api-staging-eu.artera.ai',
      auth0Domain: 'staging-auth-eu.artera.ai',
      auth0ClientId: 'mb0MiiZYAs4f1Cv8bD9UWar0GEOjKIK0',
      auth0Audience: 'https://api-staging-eu.artera.ai',
      auth0Namespace: 'https://app-eu.artera.ai',
      auth0Connection: 'Username-Password-Authentication',
      sentryTracesSampleRate: 1.0,
      sentryEnvironment: 'react-samd-eu-staging',
    },
    [App.SamdUk]: {
      apiBaseUrl: 'https://api-staging-uk.artera.ai',
      auth0Domain: 'staging-auth-uk.artera.ai',
      auth0ClientId: '2Ce3AUMnJTZx7jUc67w64ZzV8Ah23LHg',
      auth0Audience: 'https://api-staging-uk.artera.ai',
      auth0Namespace: 'https://app-uk.artera.ai',
      auth0Connection: 'Username-Password-Authentication',
      sentryTracesSampleRate: 1.0,
      sentryEnvironment: 'react-samd-uk-staging',
    },
    [App.SamdDemo]: {
      apiBaseUrl: 'https://api-staging-demo-us.artera.ai',
      auth0Domain: 'staging-auth.artera.ai',
      auth0ClientId: 'yOF3pliC1LfppjwlVCNpC29G28HmiZvi',
      auth0Audience: 'https://api-staging-demo-us.artera.ai',
      auth0Namespace: 'https://app.artera.ai',
      auth0Connection: 'demo-artera-user-password-auth',
      sentryTracesSampleRate: 1.0,
      sentryEnvironment: 'react-samd-us-staging',
    },
  },
  [Environment.Production]: {
    [App.Ldt]: {
      apiBaseUrl: 'https://api.artera.ai',
      auth0Domain: 'auth.artera.ai',
      auth0ClientId: 'nOYYcudBU9KHiQ9iKZ2ZcNW5GTe13Uu7',
      auth0Audience: 'https://api.artera.ai',
      auth0Namespace: 'https://app.artera.ai',
      auth0Connection: 'artera-user-password-auth',
      sentryTracesSampleRate: 0.5,
      sentryEnvironment: 'react-ldt-production',
    },
    [App.SamdUs]: {
      apiBaseUrl: 'https://api-us.artera.ai',
      auth0Domain: 'auth.artera.ai',
      auth0ClientId: 'aw3LQWUjrOOWBpLs7FlvN7Pa1N7IoONM',
      auth0Audience: 'https://api-us.artera.ai',
      auth0Namespace: 'https://app.artera.ai',
      auth0Connection: 'samd-artera-user-password-auth',
      sentryTracesSampleRate: 0.5,
      sentryEnvironment: 'react-samd-us-production',
    },
    [App.SamdEu]: {
      apiBaseUrl: 'https://api-eu.artera.ai',
      auth0Domain: 'auth-eu.artera.ai',
      auth0ClientId: 'twc1Mzcg3vElx8W5E76KZUCCwgp5fhix',
      auth0Audience: 'https://api-eu.artera.ai',
      auth0Namespace: 'https://app-eu.artera.ai',
      auth0Connection: 'Username-Password-Authentication',
      sentryTracesSampleRate: 0.5,
      sentryEnvironment: 'react-samd-eu-production',
    },
    [App.SamdUk]: {
      apiBaseUrl: 'https://api-uk.artera.ai',
      auth0Domain: 'auth-uk.artera.ai',
      auth0ClientId: 'FpqEPyqfaTsqrKmw3iZQgMIY70SD1bok',
      auth0Audience: 'https://api-uk.artera.ai',
      auth0Namespace: 'https://app-uk.artera.ai',
      auth0Connection: 'Username-Password-Authentication',
      sentryTracesSampleRate: 0.5,
      sentryEnvironment: 'react-samd-uk-production',
    },
    [App.SamdDemo]: {
      apiBaseUrl: 'https://api-demo-us.artera.ai',
      auth0Domain: 'auth.artera.ai',
      auth0ClientId: 'K80WtolV91Ip5wgX4WZ73SFiceumNApC',
      auth0Audience: 'https://api-demo-us.artera.ai',
      auth0Namespace: 'https://app.artera.ai',
      auth0Connection: 'demo-artera-user-password-auth',
      sentryTracesSampleRate: 1.0,
      // TODO(Troy): confirm we want to set up sentry for samd demo or reuse samd us configs
      sentryEnvironment: 'react-samd-us-production',
    },
  },
};

export function getConfig() {
  return configMap[getEnvironment()]![getApp()]!;
}
